import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { formatCurrencyPrice } from '../../utils/currency';
import { formatDateTime } from '../../utils/dates';
import { useTheme } from '../../contexts/ThemeContext';

echarts.use([
  LineChart,
  GridComponent,
  TooltipComponent,
  MarkLineComponent,
  CanvasRenderer,
]);

export interface PriceHistoryPoint {
  price: string;
  list_price?: string | null;
  recorded_at: string;
}

export interface PriceHistoryChartProps {
  points: PriceHistoryPoint[];
  currency: string;
  targetPrice?: string | null;
  className?: string;
}

export const PriceHistoryChart: React.FC<PriceHistoryChartProps> = ({
  points,
  currency,
  targetPrice,
  className = 'h-72 w-full',
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chart = chartInstance.current;

    // Palette tokens based on active theme
    const textColor = isDark ? '#94a3b8' : '#64748b'; // slate-400 : slate-500
    const splitLineColor = isDark ? 'rgba(51, 65, 85, 0.4)' : 'rgba(226, 232, 240, 0.8)'; // slate-700 / slate-200
    const tooltipBg = isDark ? '#0f172a' : '#ffffff'; // slate-900 / white
    const tooltipBorder = isDark ? '#334155' : '#e2e8f0';
    const tooltipTextColor = isDark ? '#f8fafc' : '#0f172a';

    // Sort observations chronologically
    const sorted = [...points].sort(
      (a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
    );

    const xData = sorted.map((p) => p.recorded_at);
    const yData = sorted.map((p) => parseFloat(p.price));

    const numericTarget = targetPrice ? parseFloat(targetPrice) : null;

    // Calculate resilient axis boundaries with padding for flat lines and single observations
    const validPrices = yData.filter((p) => !isNaN(p) && p > 0);
    const minPrice = validPrices.length > 0 ? Math.min(...validPrices, numericTarget ?? Infinity) : 0;
    const maxPrice = validPrices.length > 0 ? Math.max(...validPrices, numericTarget ?? -Infinity) : 100;

    let yAxisMin = minPrice;
    let yAxisMax = maxPrice;
    if (minPrice === maxPrice) {
      yAxisMin = Math.max(0, Math.floor(minPrice * 0.95));
      yAxisMax = Math.ceil(maxPrice * 1.05);
    } else {
      const diff = maxPrice - minPrice;
      yAxisMin = Math.max(0, Math.floor(minPrice - diff * 0.1));
      yAxisMax = Math.ceil(maxPrice + diff * 0.1);
    }

    const option: echarts.EChartsCoreOption = {
      grid: {
        top: 24,
        right: 20,
        bottom: 30,
        left: 75,
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: tooltipBg,
        borderColor: tooltipBorder,
        textStyle: {
          color: tooltipTextColor,
          fontSize: 12,
        },
        formatter: (params: any) => {
          if (!params || !params[0]) return '';
          const p = params[0];
          const point = sorted[p.dataIndex];
          if (!point) return '';

          const dateStr = formatDateTime(point.recorded_at);
          const priceStr = formatCurrencyPrice(point.price, currency);
          let html = `<div class="font-sans text-xs">
            <div class="text-slate-400 mb-1">${dateStr}</div>
            <div class="font-bold text-sm text-emerald-500">${priceStr}</div>`;

          if (point.list_price && parseFloat(point.list_price) > parseFloat(point.price)) {
            const listStr = formatCurrencyPrice(point.list_price, currency);
            html += `<div class="text-xs text-slate-400 line-through">List: ${listStr}</div>`;
          }

          if (numericTarget && !isNaN(numericTarget)) {
            const targetStr = formatCurrencyPrice(numericTarget, currency);
            html += `<div class="text-xs text-indigo-400 mt-1">Target: ${targetStr}</div>`;
          }

          html += `</div>`;
          return html;
        },
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLine: { lineStyle: { color: splitLineColor } },
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: (value: string) => {
            const d = new Date(value);
            return isNaN(d.getTime()) ? value : `${d.getMonth() + 1}/${d.getDate()}`;
          },
        },
      },
      yAxis: {
        type: 'value',
        min: yAxisMin,
        max: yAxisMax,
        splitLine: { lineStyle: { color: splitLineColor, type: 'dashed' } },
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: (value: number) => formatCurrencyPrice(value, currency),
        },
      },
      series: [
        {
          name: 'Price',
          type: 'line',
          data: yData,
          smooth: true,
          showSymbol: sorted.length <= 15,
          symbolSize: 6,
          itemStyle: {
            color: '#10b981', // emerald-500
          },
          lineStyle: {
            width: 2.5,
            color: '#10b981',
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(16, 185, 129, 0.28)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.01)' },
            ]),
          },
          markLine:
            numericTarget && !isNaN(numericTarget)
              ? {
                  silent: true,
                  symbol: ['none', 'none'],
                  data: [
                    {
                      yAxis: numericTarget,
                      lineStyle: {
                        color: '#6366f1', // indigo-500
                        type: 'dashed',
                        width: 1.5,
                      },
                      label: {
                        formatter: `Target: ${formatCurrencyPrice(numericTarget, currency)}`,
                        position: 'insideEndTop',
                        color: '#818cf8',
                        fontSize: 11,
                      },
                    },
                  ],
                }
              : undefined,
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);
    const resizeObserver = new ResizeObserver(() => {
      chart.resize();
    });
    resizeObserver.observe(chartRef.current);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [points, currency, targetPrice, isDark]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, []);

  if (points.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center border border-dashed border-slate-200 dark:border-slate-800 rounded-xl p-8 text-center text-slate-400 ${className}`}>
        <p className="text-sm font-medium">No price history observations yet</p>
        <p className="text-xs text-slate-500 mt-1">Price observations will appear here once tracked periodically.</p>
      </div>
    );
  }

  return <div ref={chartRef} className={className} data-testid="price-history-chart" />;
};
